import Loading from "components/common/Loading";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Notice } from "type/type";
import styled from "styled-components";
import hubLayout from 'styles/css/hubLayout.module.css';
import { rootFixedSize } from "styles/theme";
import { EmptyDataComponent } from "components/common/EmptyText";
import Pagenation from "components/common/Pagenation";
import { CiEdit } from "react-icons/ci";
import { calculDayDateToString, formatDateToString, formattedDateTime } from 'pages/utils/commonUtils';
import { GoPaperclip } from "react-icons/go";
import { createNotice, deleteNotice, getNoticeAll, getNoticeOne, maxLengthCheck, putNotice } from "api/api";
import { useForm } from "react-hook-form";
import { PrimaryButton } from "components/common/Button";
import { AdminLoginInfoContext } from "pages/Layout";
import imageCompression from 'browser-image-compression';

const InputContainer = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  input, select {
    padding: ${rootFixedSize.inputShortPadding};
    border-radius: ${rootFixedSize.inputBorderRadius};
    border: none;
    &:focus-visible {
      outline-color: ${prop => prop.theme.borderAccent};
    }
  }
    select {
      &:hover {
        cursor: pointer;
      }
    }
    svg {
      width: 1.25rem;
      height: 1.25rem;
      &:hover {
        cursor: pointer;
      opacity: 0.7;
    }
  }
  button {
    margin-left: auto;
  }
`;
const CommonListWrap = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: auto;
  summary {
    min-height: 3rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    align-items: center;
    gap: 1rem;
    border-radius: 0.5rem;
    background-color: ${prop => prop.theme.tableHeadBgColor};
    &:hover {
      background-color: ${prop => prop.theme.tableBodyTrHoverBgColor};
      cursor:pointer;
    }
    p:nth-of-type(2) {
      margin-left: auto;
    }
  }
  details[open] {
    summary{
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
    nav {
      white-space: break-spaces;
      padding: 1rem;
      min-height: 2.5rem;
      max-height: 30rem;
      overflow-y: auto;
      border-bottom-right-radius: 0.5rem;
      border-bottom-left-radius: 0.5rem;
      background-color: ${prop => prop.theme.tableBodyTrHoverBgColor};
      .content-etc {
        padding-top: 1rem;
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
        font-size: 13px;
        span:last-of-type {
          margin-left: auto;
        }
      }
    }
    & img {
      width: 100%;
      border-radius: 1rem;
      height: 25rem;
    }
  }
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
    &:last-of-type {
      background-color: ${prop => prop.theme.layer3BgColor};
      border-radius: 0.5rem;
    }
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }
`;
const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;
const FormComponent = styled.form`
  display: flex;
  flex-direction: column;
  .error-msg-wrap {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    p {
      text-align: right;
      width: 100%;
    }
  }
`;
const InputWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e1e1e1;
  .input-wrap {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
    position: relative;
    label:first-of-type {
      width: 6.25rem;
      text-align: left;
    }
  }
  label, input, select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  input, select, textarea {
    border: none;
    background-color: #f1f1f1;
    border-radius: 0.25rem;
    font-family: var(--font-pretendard-medium);
    &:focus-visible {
      outline: none;
      &:not(:disabled) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
  }
  input:disabled {
    background-color: ${prop => prop.theme.readOnlyColor};
    color: ${prop => prop.theme.rootTextColor};
    cursor: auto;
  }
  .notice-title-wrap,
  .notice-content-wrap {
    width: 100%;
    input, textarea {
      width: calc(100% - 6.25rem);
    }
  }
  textarea {
    resize: none;
    min-height: 6.25rem;
  }
`;


// 게시판 상세보기
export function AdminNoticeDetail() {
  const { adminLoginInfo } = useContext(AdminLoginInfoContext);
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  const { noticeIdx } = useParams();
  const [loading, setLoading] = useState(false);
  
  // useForm
  let { register, formState: { errors, isValid } } = useForm<any>({
    mode: 'all',
  });

  const [noticeData, setNoticeData] = useState<Notice>({
    idx: 0, 
    write_id: adminLoginInfo.id, 
    type: "normal", 
    title: "", 
    content: "", 
    start_date: `${formatDateToString(new Date())} 00:00:00`,
    end_date: `${calculDayDateToString(new Date(), 7)} 23:59:59`, 
    no_show_days: 1, 
    redirect_url: "", 
    is_use: 1, 
    create_date: "", 
    update_date: "",
    send_file: "",
    original_file_name: "", 
  });

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    let _value = (name === "is_use" || name === "no_show_days") ? Number(value) : value;
    setNoticeData((prevData) => ({
      ...prevData,
      [name]: _value,
    }));
  };

  // 공지사항 등록
  const submitNotice = async (data: Notice) => {
    if (!data.title) {
      alert('제목을 입력해주세요.');
      return;
    }

    const fileData = new FormData();

    const fields: Record<string, string | Blob> = {
      title: data.title || '',
      content: data.content || '',
      write_id: adminLoginInfo.id || '',
      type: data.type || '',
      start_date: data.start_date || '',
      end_date: data.end_date || '',
      original_file_name: data.original_file_name || '',
      no_show_days: String(data.no_show_days || '1'),
      redirect_url: data.redirect_url || '',
      is_use: String(data.is_use ?? '1'),
    };

    Object.entries(fields).forEach(([key, value]) => {
      fileData.append(key, value);
    });

    if (data.file) {
      fileData.append('file', data.file);
    }
    const result = data.idx === 0
      ? await createNotice(fileData)
      : await putNotice(data.idx, fileData);

    if (result?.code === 200) {
      alert(`공지사항이 ${data.idx === 0 ? '등록' : '수정'}되었습니다.`);
      navigate('/admin/notice/list');
    } else {
      alert(result.message);
    }
  };

  // 파일 관련 시작 -----------------------
  const fileInput = useRef<HTMLInputElement>(null);

  // 파일 선택 input 클릭 시
  const handleButtonClick = (e: any) => {
    fileInput.current?.click();
  };

  // 이미지 압축 함수
  const compressImageFile = async (file: File) => {
    const options = {
      maxSizeMB: 1, // 최대 1MB
      useWebWorker: true,
      preserveExif: true,
    };
    try {
      const compressedFile = await imageCompression(file, options);
      const base64 = await imageCompression.getDataUrlFromFile(compressedFile);
      return { compressedFile, base64 };
    } catch (error) {
      console.error('이미지 압축 실패:', error);
      return null;
    }
  };

  // 파일 변경 핸들러
  const fileHandleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const file = e.target.files?.[0];
    if (file) {
      const fileName = file.name;

      // 압축 적용
      const result = await compressImageFile(file);
      e.target.value = ""; // 같은 파일 선택 시에도 변경 이벤트 발생하게
      if (result) {
        const { compressedFile } = result;
        setNoticeData((prevData) => ({
          ...prevData,
          file: compressedFile,
          original_file_name: fileName,
        }));
      }
    }
    setLoading(false);
  };

  // 파일삭제 버튼 클릭 시 파일삭제
  const handleDeleteFile = (e: any) => {
    setNoticeData((prevData) => ({
      ...prevData,
      original_file_name: "",
    }));
  }

  // 파일 관련 끝 -----------------------
  // 삭제
  async function handleDeleteNotice() {
    if (!noticeData.idx) return;
    const isDelete = window.confirm('공지사항을 삭제하시겠습니까?');
    if (!isDelete) return;
    const result = await deleteNotice(noticeData.idx);
    const goList = result.code === 200 ? true : false;
    if(goList) {
      alert('삭제가 완료되었습니다.');
      navigate('/admin/notice/list');
    } else {
      alert(result.message);
    }
  }

  const getNotice = async () => {
    const response = await getNoticeOne(Number(noticeIdx));
    if (response.code === 200) {
      response.data.start_date = formattedDateTime(response.data.start_date);
      response.data.end_date = formattedDateTime(response.data.end_date);
      response.data.create_date = formattedDateTime(response.data.create_date);
      response.data.update_date = formattedDateTime(response.data.update_date);
      setNoticeData(response.data);
      isValid = true;
    } else {
      alert(response.message);
    }
  };

  function handleToNumericValue(event: any) {
    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    event.target.value = numericValue;
    handleChange(event);
  }

  useEffect(() => {
    if (Number(noticeIdx) > 0) {
      getNotice();
    }
  },[noticeIdx]);
  return (
    <>
      <Container>
        <TitleAndBtnContainer>
          <div>
            <p className='title-h2'>
              <span>공지사항</span>
            </p>
          </div>
          <div>
            <button type="button" className='default-btn edit-btn' onClick={() => submitNotice(noticeData)} disabled={!isValid}>
              {noticeData.idx === 0 ? `등록` : `수정`}
            </button>
            {!!noticeData.idx && <button type="button" className='default-btn delete-btn' onClick={handleDeleteNotice}>삭제</button>}
            <button type="button" className='default-btn list-btn' onClick={() => navigate('/admin/notice/list')}>목록</button>
          </div>
        </TitleAndBtnContainer>
          <div>
            <FormComponent name='detailInfoForm' id='detailInfoForm'>
              <InputWrap>
                <div className="input-wrap">
                  <label htmlFor='admin_id'>작성자</label>
                  <input type='text' id='admin_id' value={noticeData.write_id} disabled 
                    {...register("admin_id")}
                  />
                </div>
                <div className="input-wrap">
                  <label htmlFor='create_date'>등록일자</label>
                  <input type='text' id='create_date' value={ noticeData.create_date } disabled
                    {...register("create_date")}
                  />
                </div>
                <div className="input-wrap">
                  <label htmlFor='update_date'>수정일자</label>
                  <input type='text' id='update_date' value={ noticeData.update_date } disabled 
                    {...register("update_date")}
                  />
                </div>
              </InputWrap>
              <InputWrap>
                <div className="input-wrap">
                  <label htmlFor='type'>공지타입</label>
                  <select id='type' value={ noticeData.type }
                    {...register("type", {
                      required: true,
                      onChange: handleChange
                    })}
                  >
                    <option value='normal'>일반</option>
                    <option value='popup'>팝업</option>
                  </select>
                </div>
                <div className="input-wrap">
                  <label htmlFor='start_date'>시작일자</label>
                  <input type='datetime-local' id='start_date' value={ noticeData.start_date }
                    {...register("start_date", {
                      required: true,
                      onChange: handleChange
                    })}
                  />
                </div>
                <div className="input-wrap">
                  <label htmlFor='end_date'>종료일자</label>
                  <input type='datetime-local' id='end_date' value={ noticeData.end_date }
                    {...register("end_date", {
                      required: true,
                      onChange: handleChange
                    })}
                  />
                </div>
              </InputWrap>
              {noticeData.type === 'popup' &&
                <InputWrap>
                  <div className="input-wrap">
                    <label htmlFor='no_show_days'>팝업공지</label>
                    <input type='text' id='no_show_days' value={ noticeData.no_show_days } style={{ width: '3rem', textAlign: 'center' }} maxLength={2}
                      {...register("no_show_days", {
                        required: true,
                        onChange: (e) => {
                          maxLengthCheck(e);
                          handleToNumericValue(e);
                        }
                      })}
                    />
                  </div>
                  <div className="input-wrap">
                    <label style={{ width: 'auto' }}>일동안 보지않기</label>
                  </div>
                </InputWrap>
              }
              <InputWrap>
                <div className='input-wrap notice-title-wrap'>
                  <label>제목</label>
                  <input type='text' placeholder={`제목 입력`} value={noticeData.title} maxLength={50}
                    {...register("title", {
                      required: false,
                      onChange: (e) => {
                        maxLengthCheck(e);
                        handleChange(e);
                      }
                    })}
                  />
                </div>
              </InputWrap>
              <InputWrap>
                <div className='error-msg-wrap'>
                  <div className='input-wrap notice-content-wrap'>
                    <label>내용</label>
                    <textarea placeholder={`내용을 입력하세요.`} value={noticeData.content} maxLength={200}
                      {...register("content", {
                        onChange: (e) => {
                          maxLengthCheck(e);
                          handleChange(e);
                        }
                      })}
                    >
                    </textarea>
                  </div>
                </div>
              </InputWrap>
              <InputWrap>
                <div className='input-wrap notice-title-wrap'>
                  <label>링크 설정</label>
                  <input type='text' placeholder={`이미지 클릭 시 이동 할 URL`} value={noticeData.redirect_url} maxLength={100}
                    {...register("redirect_url", {
                      required: false,
                      pattern: {
                        value: /^https?:\/\//g,
                        message: 'https:// 또는 http://를 넣어주세요.'
                      },
                      onChange: (e) => {
                        maxLengthCheck(e);
                        handleChange(e);
                      }
                    })}
                  />
                </div>
                {errors.redirect_url && <p className={`${errors.redirect_url && hubLayout.tx_red}`}>{String(errors.redirect_url?.message)}</p>}
              </InputWrap>
              <InputWrap>
                  <div className="input-wrap">
                    <label htmlFor='is_use'>게시여부</label>
                    <select name="is_use" id="is_use" value={noticeData.is_use} onChange={handleChange}>
                      <option value={"1"}>게시</option>
                      <option value={"0"}>숨김</option>
                    </select>
                  </div>
                  {loading ?
                    <>
                      <img src="/image/Spinner.svg" alt="loading" style={{ width: "40px" }} />
                      <div style={{ padding: "0.5rem" }}>파일을 1MB 이하로 압축중입니다.</div>
                    </>
                  :
                    <div className='input-wrap file-wrap' onClick={handleButtonClick}>
                      <label htmlFor='file_name'>첨부파일</label>
                      <button type='button' className='default-btn edit-btn'>파일선택</button>
                      <input type="file" name="file" id="file_name" onChange={fileHandleChange} accept='.jpg, .png, .jpeg, .mp4' style={{ display: "none" }} ref={fileInput} />
                      <div title={`${noticeData.original_file_name}`}>
                        선택된 파일: 
                        {!noticeData.original_file_name ? '파일을 선택하세요.' 
                          : (noticeData.original_file_name).length > 40 
                          ? `${(noticeData.original_file_name).substring(0, 40)}...${(noticeData.original_file_name).substring((noticeData.original_file_name).lastIndexOf('.'))}`
                          : noticeData.original_file_name}
                      </div>
                    </div>
                  }
                  <button type='button' className='default-btn delete-btn' onClick={handleDeleteFile}>파일 삭제</button>
              </InputWrap>
            </FormComponent>
          </div>
      </Container>
    </>
  );
}

// 게시판 목록
export function AdminNoticeList() {
  const [loading, setLoading] = useState(true);
  const [noticeList, setNoticeList] = useState<Notice[]>([]);

  // 신규 등록/상세보기 버튼
  const navigate = useNavigate();
  function navigatorNoticeDetail(idx: number, data?: Notice) {
    navigate(`/admin/notice/detail/${idx}`);
  }
  // 페이지네이션
  const [limit] = useState(10); // 보여줄 게시물 개수
  const [page, setPage] = useState(1); // 현재 페이지
  const buttonPerPage = 10; // 보여줄 페이지네이션 버튼 수
  const [totalCount, setTotalCount] = useState(0);
  const numPages = Math.ceil(totalCount / limit); // 총 페이지 수
  const [startButtonNum, setStartButtonNum] = useState(1); // 버튼 시작 숫자
  const [endButtonNum, setEndButtonNum] = useState(buttonPerPage); // 버튼 끝 숫자

  // 페이지네이션 버튼 클릭 시
  const isClickButton = (str:string) => {
    if (str === 'prev') handlePrevPage();
    else handleNextPage();
  };

  // 이전페이지 이동(buttonPerPage만큼 -)
  const handlePrevPage = () => {
    const prevPage = Math.max(startButtonNum - buttonPerPage, 1);
    setStartButtonNum(prevPage);
    setEndButtonNum(prevPage + buttonPerPage - 1);
    setPage(prevPage);
  };

  // 다음페이지 이동(buttonPerPage만큼 +)
  const handleNextPage = () => {
    const nextPage = Math.min(startButtonNum + buttonPerPage, numPages);
    setStartButtonNum(nextPage);
    setEndButtonNum(nextPage + buttonPerPage - 1);
    setPage(nextPage);
  };
  
  // 공지사항 리스트 가져오기
  const getNoticeList = async (pageNo: number, pageSize: number) => {
    try {
      setLoading(true);
      const result = await getNoticeAll(pageNo, pageSize);
      if (result.code === 200) {
        setNoticeList(result.data);
        setTotalCount(result.total_count);
      }
      setLoading(false);
    } catch (error) {
      console.error("error ", error);
    }
  }

  // 처음 useEffect 실행 및 페이지 변경 시 실행
  useEffect(() => {
    getNoticeList(page, limit);
  }, [page, limit]);
  return (
    <>
      <InputContainer>
        <div>
          <label>검색 결과 : <span style={{fontWeight:"600"}}>{totalCount.toLocaleString('ko-KR')}</span>건</label>
        </div>
        <PrimaryButton type="button" onClick={() => navigatorNoticeDetail(0)}>등록</PrimaryButton>
      </InputContainer>
      { loading ? <Loading /> :
        <CommonListWrap>
          {noticeList.length !== 0 ? 
            noticeList.map((notice, index) => (
              <details key={index}>
                <summary>
                  <p>{(page - 1) * 10 + index + 1}. {notice.title}</p>
                  <p>{notice.send_file ? <GoPaperclip /> : ""}</p>
                  <CiEdit onClick={() => navigatorNoticeDetail(notice.idx, notice)} />
                </summary>
                <nav>
                  {(notice.redirect_url && notice.send_file) ?
                    <p>
                      <a href={notice.redirect_url} target="_blank" rel="noopener noreferrer">
                        <img src={notice.send_file} alt="photo" />
                      </a>
                    </p>
                    :
                    notice.send_file &&
                      <p><img src={notice.send_file} alt="photo" /></p>
                  }
                  {notice.content && <p>{notice.content}</p>}
                  {!notice.send_file && <p><a href={notice.redirect_url} target="_blank" rel="noopener noreferrer">{notice.redirect_url}</a></p>}
                  <div className="content-etc">
                    <span>{notice.create_date ? formattedDateTime(notice.create_date) : ""}</span>
                    <span>{`작성자 : ${notice.write_id}`}</span>
                  </div>
                </nav>
              </details>
            ))
            : <EmptyDataComponent />
          }
        </CommonListWrap>
      }
      <Pagenation
        totalCount={totalCount}
        total={numPages}
        limit={limit}
        page={page}
        setPage={setPage}
        startButtonNum={startButtonNum}
        setClickButton={isClickButton}
      />
    </>
  );
}